import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CONFIG } from '../../../config'
import { useAuth } from '../../../contexts/AuthContext'
import { useFetch } from '../../useFetch'

import { ApiError } from '../../../types/exception/errorStatus'
import {
  SliceResponse,
  SortDirectionType,
  SortType,
} from '../../../types/listing'
import { PartnerModel } from '../../../types/court/court'
import { PartnerStatus } from '../../../types/court/court'
import { WidgetDataResult } from '../../../types/widget/widget'
import { getErrorMessage } from '../../../utils/exception/exceptionUtils'

export const useSavedPartnersWidget = (): WidgetDataResult<PartnerModel[]> => {
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  const { t } = useTranslation('exceptions')
  const [data, setData] = useState<PartnerModel[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    const fetchSavedPartners = async () => {
      setLoading(true)
      setError(null)

      const query = new URLSearchParams({
        page: '0',
        size: '5',
        status: PartnerStatus.LINKED,
        sort: SortType.DATE,
        direction: SortDirectionType.ASC,
        search: '',
      })

      try {
        const response = await fetcher<SliceResponse<PartnerModel>>(
          `${CONFIG.BOUNCE_API}/api/v1/partners?${query.toString()}`
        )
        if (active) setData(response?.content ?? [])
      } catch (caughtError) {
        if (active) {
          setError(
            caughtError instanceof ApiError
              ? getErrorMessage(caughtError.data)
              : t('exceptions:unknown')
          )
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    void fetchSavedPartners()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInfo?.accessToken, t])

  return { data, loading, error }
}
